import React, { useEffect, useState } from 'react'
import { getDevicePushStatus, registerPushToken } from '../../async/push'
import PushBlockedModal from './push-blocked-modal'
import PushEnableModal from './push-enable-modal'

// Per-device memory, so the prompts don't reappear on every visit.
const PROMPT_SNOOZED_UNTIL_KEY = 'wcid_push_prompt_snoozed_until'
const BLOCKED_NOTICE_SHOWN_KEY = 'wcid_push_blocked_notice_shown'
export const PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000 // "Not now" → ask again in a week

// storage can throw (private mode, blocked site data) — treat that as "nothing stored"
const readStorage = (key: string) => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}
const writeStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // ignore: we'll just ask again next time
  }
}

const isPromptSnoozed = () => Number(readStorage(PROMPT_SNOOZED_UNTIL_KEY) || 0) > Date.now()
const snoozePrompt = () => writeStorage(PROMPT_SNOOZED_UNTIL_KEY, String(Date.now() + PROMPT_SNOOZE_MS))
const blockedNoticeShown = () => readStorage(BLOCKED_NOTICE_SHOWN_KEY) === '1'
const markBlockedNoticeShown = () => writeStorage(BLOCKED_NOTICE_SHOWN_KEY, '1')

// Accounts already checked in this page session (survives header remounts on navigation)
const checkedAccounts = new Set<string>()

/** Test-only: forget which accounts were checked this session. */
export const resetPushDeviceCheck = () => checkedAccounts.clear()

/**
 * When the account has push turned on, make sure *this device* can actually receive
 * pushes, once per session:
 *   missing (permission granted, no subscription) → re-register silently
 *   prompt  (never asked)                          → ask via PushEnableModal
 *   denied  (blocked)                              → explain once via PushBlockedModal
 *   subscribed / unsupported                       → nothing
 */
const PushDeviceCheck = ({
  accountId,
  pushEnabled
}: {
  accountId?: string
  pushEnabled: boolean
}) => {
  const [enableOpen, setEnableOpen] = useState(false)
  const [blockedOpen, setBlockedOpen] = useState(false)
  const [enabling, setEnabling] = useState(false)

  useEffect(() => {
    if (!accountId || !pushEnabled || checkedAccounts.has(accountId)) return
    checkedAccounts.add(accountId)

    let cancelled = false
    void getDevicePushStatus().then(status => {
      if (cancelled) return
      if (status === 'missing') {
        // Permission already granted, so this doesn't show a browser prompt
        void registerPushToken(accountId)
      } else if (status === 'prompt' && !isPromptSnoozed()) {
        setEnableOpen(true)
      } else if (status === 'denied' && !blockedNoticeShown()) {
        markBlockedNoticeShown()
        setBlockedOpen(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [accountId, pushEnabled])

  const handleEnable = async () => {
    if (!accountId) return
    setEnabling(true)
    await registerPushToken(accountId) // shows the browser's permission prompt
    setEnabling(false)
    setEnableOpen(false)

    const permission = typeof Notification === 'undefined' ? 'default' : Notification.permission
    if (permission === 'denied') {
      markBlockedNoticeShown()
      setBlockedOpen(true)
    } else if (permission === 'default') {
      // They dismissed the browser prompt without choosing — treat as "not now"
      snoozePrompt()
    }
  }

  const handleDismiss = () => {
    snoozePrompt()
    setEnableOpen(false)
  }

  return (
    <>
      <PushEnableModal
        open={enableOpen}
        enabling={enabling}
        onEnable={() => void handleEnable()}
        onDismiss={handleDismiss}
      />
      <PushBlockedModal open={blockedOpen} onClose={() => setBlockedOpen(false)} />
    </>
  )
}

export default PushDeviceCheck
